import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Users, BedDouble, CalendarCheck, Wallet, AlertTriangle, FlaskConical, TrendingUp, DoorOpen, Plus, UserPlus, Activity,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { Loader } from "@/components/Loader";
import { PaywallGate } from "@/components/PaywallGate";
import { Button } from "@/components/ui/button";
import { money, timeAgo } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";

export default function Dashboard() {
  const { user, org } = useAuth();
  const navigate = useNavigate();
  const cur = org?.currency || "NGN";

  const { data: stats, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/analytics/dashboard")).data,
  });
  const { data: appts = [] } = useQuery({
    queryKey: ["appointments-recent"],
    queryFn: async () => (await api.get("/appointments")).data || [],
  });
  const { data: notes = [] } = useQuery({
    queryKey: ["notifications-dash"],
    queryFn: async () => (await api.get("/notifications")).data || [],
  });

  if (isLoading) return <Loader full />;

  const cards = [
    { icon: Users, label: "Total Patients", value: stats?.total_patients ?? 0, tone: "blue" },
    { icon: BedDouble, label: "Admitted", value: stats?.admitted ?? 0, tone: "indigo" },
    { icon: CalendarCheck, label: "Appointments Today", value: stats?.appts_today ?? 0, tone: "emerald" },
    { icon: Wallet, label: "Revenue", value: money(stats?.revenue, cur), tone: "emerald" },
    { icon: TrendingUp, label: "Outstanding", value: money(stats?.outstanding, cur), tone: "amber" },
    { icon: AlertTriangle, label: "Low Stock", value: stats?.low_stock ?? 0, tone: "rose" },
    { icon: FlaskConical, label: "Pending Labs", value: stats?.pending_labs ?? 0, tone: "blue" },
    { icon: DoorOpen, label: "Beds Available", value: `${stats?.beds_available ?? 0}/${stats?.beds_total ?? 0}`, tone: "slate" },
  ];

  return (
    <PaywallGate>
      <PageHeader title={`Good day, ${user?.full_name?.split(" ")[0] || "there"}`} subtitle={`${org?.name || "Your organisation"} · Operational overview`} testId="page-dashboard">
        <Button variant="outline" className="gap-1.5 border-slate-300" onClick={() => navigate("/app/patients")} data-testid="dash-new-patient"><UserPlus className="h-4 w-4" /> New patient</Button>
        <Button className="gap-1.5 bg-teal-700 hover:bg-teal-800" onClick={() => navigate("/app/appointments")} data-testid="dash-new-appt"><Plus className="h-4 w-4" /> Appointment</Button>
      </PageHeader>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {cards.map((c, i) => <StatCard key={c.label} {...c} index={i} testId={`stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`} />)}
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <h3 className="font-display text-base font-semibold text-slate-900">Upcoming appointments</h3>
              <button onClick={() => navigate("/app/appointments")} className="text-sm font-medium text-teal-700 hover:underline">View all</button>
            </div>
            {appts.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-slate-400">No appointments scheduled.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {appts.slice(0, 6).map((a) => (
                  <div key={a.id} className="flex items-center justify-between px-5 py-3">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{a.patient_name || a.reason || "Appointment"}</p>
                      <p className="text-xs text-slate-500">{a.provider || "—"} · {timeAgo(a.date)}</p>
                    </div>
                    <StatusBadge value={a.status} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-4">
            <Activity className="h-4 w-4 text-teal-700" />
            <h3 className="font-display text-base font-semibold text-slate-900">Notifications</h3>
          </div>
          {notes.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-slate-400">You're all caught up.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {notes.slice(0, 6).map((n, i) => (
                <div key={n.id || i} className="px-5 py-3">
                  <p className="text-sm font-medium text-slate-800">{n.title || n.message || "Notification"}</p>
                  <p className="text-xs text-slate-500">{timeAgo(n.created_at)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PaywallGate>
  );
}
